import React, { useEffect, useState, useCallback } from "react";
import { Routes, Route, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, UtensilsCrossed, ChefHat, Receipt, BookOpen, BedDouble,
  CalendarRange, Users, BarChart3, Settings, LogOut, Bell, Menu, X, ConciergeBell, LayoutTemplate, Printer, FileText, PenTool,
} from "lucide-react";

import Dashboard from "@/pages/app/Dashboard";
import POS from "@/pages/app/POS";
import KDS, { playAlarm } from "@/pages/app/KDS";
import Orders from "@/pages/app/Orders";
import MenuManager from "@/pages/app/MenuManager";
import Rooms from "@/pages/app/Rooms";
import PMS from "@/pages/app/PMS";
import UsersPage from "@/pages/app/UsersPage";
import Reports from "@/pages/app/Reports";
import SettingsPage from "@/pages/app/SettingsPage";
import FloorPlan from "@/pages/app/FloorPlan";
import Invoices from "@/pages/app/Invoices";
import PrintSettings from "@/pages/app/PrintSettings";

const NAV = [
  { to: "dashboard", label: "Табло", icon: LayoutDashboard, roles: ["OWNER", "RECEPTION"], el: <Dashboard /> },
  { to: "pos", label: "Ресторант / POS", icon: UtensilsCrossed, roles: ["OWNER", "WAITRESS"], el: <POS /> },
  { to: "kds", label: "Кухня (KDS)", icon: ChefHat, roles: ["OWNER", "KITCHEN"], el: <KDS /> },
  { to: "orders", label: "Поръчки & Плащания", icon: Receipt, roles: ["OWNER", "WAITRESS"], el: <Orders /> },
  { to: "menu", label: "Меню", icon: BookOpen, roles: ["OWNER"], el: <MenuManager /> },
  { to: "floor", label: "План на масите", icon: LayoutTemplate, roles: ["OWNER"], el: <FloorPlan /> },
  { to: "pms", label: "Календар PMS", icon: CalendarRange, roles: ["OWNER", "RECEPTION"], el: <PMS /> },
  { to: "rooms", label: "Стаи", icon: BedDouble, roles: ["OWNER", "RECEPTION"], el: <Rooms /> },
  { to: "invoices", label: "Фактури", icon: FileText, roles: ["OWNER", "RECEPTION"], el: <Invoices /> },
  { to: "users", label: "Потребители", icon: Users, roles: ["OWNER"], el: <UsersPage /> },
  { to: "reports", label: "Справки", icon: BarChart3, roles: ["OWNER"], el: <Reports /> },
  { to: "settings", label: "Настройки", icon: Settings, roles: ["OWNER"], el: <SettingsPage /> },
  { to: "printing", label: "Настройки за печат", icon: Printer, roles: ["OWNER"], el: <PrintSettings /> },
];

function NotificationBell() {
  const { user } = useAuth();
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const prevIds = React.useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      if (prevIds.current) {
        const newOnes = data.filter((n) => !prevIds.current.has(n.id));
        newOnes.slice(0, 5).forEach((n) => toast.success(n.message, { duration: 10000 }));
        if (newOnes.length) playAlarm(1046, [0, 0.18, 0.36]);
      }
      prevIds.current = new Set(data.map((n) => n.id));
      setNotifs(data);
    } catch (e) { console.debug("Notification poll failed:", e?.message); }
  }, []);

  useEffect(() => {
    if (!["OWNER", "WAITRESS"].includes(user.role)) return;
    poll();
    const iv = setInterval(poll, 4000);
    return () => clearInterval(iv);
  }, [poll, user.role]);

  if (!["OWNER", "WAITRESS"].includes(user.role)) return null;

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); poll(); };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="relative p-2 rounded-xl hover:bg-cream-muted transition-colors" data-testid="notif-bell">
        <Bell className="w-5 h-5 text-forest" />
        {notifs.length > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-destructive text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center" data-testid="notif-badge">{notifs.length}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-card border border-gold/20 z-50 overflow-hidden">
          <div className="p-3 border-b border-gold/10 font-semibold text-forest text-sm">Известия</div>
          <div className="max-h-96 overflow-y-auto">
            {notifs.length === 0 && <div className="p-4 text-sm text-muted-foreground">Няма нови известия</div>}
            {notifs.map((n) => (
              <div key={n.id} className="p-3 border-b border-gold/5 flex items-start gap-2 hover:bg-cream-muted">
                <ConciergeBell className="w-4 h-4 text-gold-dark mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm text-forest-deep font-medium">{n.message}</div>
                  <button onClick={() => markRead(n.id)} className="text-xs text-gold-dark hover:text-forest mt-1">Отбележи като прочетено</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user.role));

  const doLogout = async () => { await logout(); nav("/login"); };

  const Sidebar = (
    <aside className="w-64 bg-forest-deep text-cream flex flex-col h-full">
      <div className="p-5 border-b border-gold/15"><Logo /></div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {items.map((n) => (
          <NavLink key={n.to} to={`/app/${n.to}`} onClick={() => setMobileOpen(false)}
            className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-colors ${isActive ? "bg-gold text-forest-deep font-semibold" : "text-cream/80 hover:bg-forest-light"}`}
            data-testid={`nav-${n.to}`}>
            <n.icon className="w-5 h-5" /> {n.label}
          </NavLink>
        ))}
        {user.role === "OWNER" && (
          <a href="/editor" className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm bg-gold/15 text-gold-light hover:bg-gold hover:text-forest-deep transition-colors mt-2" data-testid="nav-edit-website">
            <PenTool className="w-5 h-5" /> Edit Website · Редактор
          </a>
        )}
      </nav>
      <div className="p-3 border-t border-gold/15">
        <div className="px-4 py-2 text-xs text-cream/50">{user.full_name || user.username} · {user.role}</div>
        <button onClick={doLogout} className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-cream/80 hover:bg-forest-light w-full" data-testid="logout-btn">
          <LogOut className="w-5 h-5" /> Изход
        </button>
      </div>
    </aside>
  );

  return (
    <div className="h-screen flex bg-cream overflow-hidden">
      <div className="hidden lg:block">{Sidebar}</div>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="relative">{Sidebar}</div>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gold/10 flex items-center justify-between px-4 shrink-0">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="app-mobile-menu"><Menu className="text-forest" /></button>
          <div className="font-display text-xl text-forest-deep">Върбица · Управление</div>
          <div className="flex items-center gap-3"><NotificationBell /></div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <ErrorBoundary key={location.pathname}>
            <Routes>
              {items.map((n) => <Route key={n.to} path={n.to} element={n.el} />)}
              <Route path="*" element={<Navigate to={`/app/${items[0]?.to || "dashboard"}`} replace />} />
            </Routes>
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}
